import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Plus, Trash2, ShieldCheck, Eye, HardHat } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import BrandingCard from "@/components/BrandingCard";

const ROLE_META = {
  owner: { icon: ShieldCheck, label: "Owner", color: "bg-slate-900 text-white" },
  inspector: { icon: HardHat, label: "Inspector", color: "bg-blue-100 text-blue-700" },
  viewer: { icon: Eye, label: "View-only", color: "bg-slate-100 text-slate-600" },
};

export default function Team() {
  const { user, org } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const isOwner = user?.role === "owner";

  const load = () => api.get("/team").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team/invite", form);
      setForm({ name: "", email: "", password: "", role: "viewer" });
      setOpen(false);
      toast.success("Member added");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/team/${id}`); load(); } catch (err) { toast.error(apiErr(err)); }
  };

  const viewers = members.filter((m) => m.role === "viewer").length;
  const inspectors = members.filter((m) => m.role === "inspector").length;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-900">Team & Seats</h1>
          <p className="text-sm text-slate-500">Inspectors: {inspectors}/{org?.seat_limit_inspectors} · View-only: {viewers}/{org?.seat_limit_viewers}</p>
        </div>
        {isOwner && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="invite-member-btn" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow hover:bg-blue-700">
                <Plus className="h-4 w-4" /> Add Member
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display uppercase">Add Team Member</DialogTitle></DialogHeader>
              <form onSubmit={invite} className="space-y-4">
                <input data-testid="member-name-input" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                <input data-testid="member-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                <input data-testid="member-password-input" required type="text" placeholder="Temp password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600" />
                <select data-testid="member-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600">
                  <option value="viewer">View-only (manager / bank)</option>
                  <option value="inspector">Inspector (can edit)</option>
                </select>
                <DialogFooter>
                  <button data-testid="save-member-btn" className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700">Add member</button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isOwner && <BrandingCard />}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {members.map((m) => {
          const meta = ROLE_META[m.role] || ROLE_META.viewer;
          return (
            <div key={m.id} data-testid={`member-row-${m.id}`} className="flex items-center justify-between border-b border-slate-100 px-5 py-4 last:border-0">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 font-display text-lg font-bold text-slate-600">{m.name?.[0]?.toUpperCase()}</div>
                <div>
                  <div className="font-semibold text-slate-900">{m.name}</div>
                  <div className="text-xs text-slate-500">{m.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${meta.color}`}><meta.icon className="h-3 w-3" />{meta.label}</span>
                {isOwner && m.role !== "owner" && (
                  <button data-testid={`remove-member-${m.id}`} onClick={() => remove(m.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="flex items-center gap-2 text-xs text-slate-500"><Users className="h-3.5 w-3.5" /> Need more seats? Upgrade your plan on the Billing page.</p>
    </div>
  );
}
